import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import inventoryApi from '../../../services/inventoryApi';;
import RouteForm from './RouteForm';

const RouteList = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('routeManagementView') || 'card';
    } catch (error) {
      return 'card';
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingRoute, setEditingRoute] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const routeStatuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'SUSPENDED', label: 'Suspended' }
  ];

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getRoutes();
      setRoutes(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching routes:', err);
      setError(err.response?.data?.message || 'Failed to fetch routes');
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewChange = (newView) => {
    setView(newView);
    try {
      localStorage.setItem('routeManagementView', newView);
    } catch (error) {
      console.warn('Failed to save view preference');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleCreateRoute = () => {
    setEditingRoute(null);
    setShowForm(true);
  };

  const handleEditRoute = (route) => {
    setEditingRoute(route);
    setShowForm(true);
  };

  const handleDeleteRoute = async (route) => {
    try {
      await inventoryApi.deleteRoute(route._id);
      setShowDeleteConfirm(null);
      await fetchRoutes();
    } catch (error) {
      console.error('Failed to delete route:', error);
      setError(error.response?.data?.message || 'Failed to delete route');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingRoute(null);
    fetchRoutes();
  };

  const clearError = () => {
    setError(null);
  };

  // Filter routes
  const filteredRoutes = routes.filter(route => {
    const matchesSearch = !searchTerm || 
      route.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      route.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (route.description && route.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || route.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getRouteIcon = (route) => {
    return route.code.substring(0, 2).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'SUSPENDED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderRouteCard = (route) => {
    return (
      <div key={route._id} className="bg-white border border-gray-200 rounded shadow-sm hover:shadow-md transition-all duration-200 group h-32 w-full">
        <div className="p-3 h-full flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center space-x-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-xs font-semibold text-purple-700 flex-shrink-0">
                {getRouteIcon(route)}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {route.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">{route.code}</p>
              </div>
            </div>

            <div className="flex items-center space-x-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button
                onClick={() => handleEditRoute(route)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                title="Edit route"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              <button
                onClick={() => setShowDeleteConfirm(route)}
                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                title="Delete route"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>

          {route.description && (
            <p className="text-xs text-gray-600 mb-2 line-clamp-2 leading-tight flex-1">
              {route.description}
            </p>
          )}

          <div className="flex flex-wrap gap-1 mt-auto">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${getStatusColor(route.status)}`}>
              {route.status}
            </span>
            {route.stops && route.stops.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                {route.stops.length} stops
              </span>
            )}
            {route.workingDays && route.workingDays.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                {route.workingDays.length} days
              </span>
            )}
          </div>
        </div>
      </div> 
    );
  }; 

  const renderRouteRow = (route) => {
    return (
      <tr key={route._id} className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center text-sm font-semibold text-purple-700">
              {getRouteIcon(route)}
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">{route.name}</div>
              <div className="text-xs text-gray-500">{route.code}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {route.stops?.length || 0} stops
          </div>
          <div className="text-xs text-gray-500">
            {route.workingDays?.length || 0} working days
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <div className="text-sm text-gray-900">
            {route.startTime || '-'} - {route.endTime || '-'}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(route.status)}`}>
            {route.status}
          </span>
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => handleEditRoute(route)}
              className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-all"
              title="Edit route"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            <button
              onClick={() => setShowDeleteConfirm(route)}
              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-all"
              title="Delete route"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded p-3">
            <div className="flex items-center">
              <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={clearError}
                className="ml-auto text-red-600 hover:text-red-500"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <RouteForm
            route={editingRoute}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowForm(false);
              setEditingRoute(null);
            }}
          />
        )}

        {/* Main Content */}
        {!showForm && (
          <div className="bg-white shadow-sm border border-gray-200">
            {/* Filters and Search */}
            <div className="px-4 sm:px-6 lg:px-8 py-2 border-b border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex flex-col lg:flex-row lg:items-center gap-2">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search routes by name or code..."
                      value={searchTerm}
                      onChange={handleSearch}
                      className="block w-full pl-10 pr-3 py-1.5 text-sm bg-white border border-gray-300 shadow-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Filters and Actions */}
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={handleStatusFilter}
                    className="text-sm border border-gray-300 rounded-sm bg-white px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-w-[140px]"
                  >
                    {routeStatuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>

                  {/* View Toggle */}
                  <div className="flex bg-gray-100 rounded-md border border-gray-200 p-0.5">
                    <button
                      onClick={() => handleViewChange('card')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'card'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="Card view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z" />
                      </svg>
                      Cards
                    </button>
                    <button
                      onClick={() => handleViewChange('list')}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all ${view === 'list'
                        ? 'bg-white text-purple-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      title="List view"
                    >
                      <svg className="w-3 h-3 mr-1 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      List
                    </button>
                  </div>

                  {/* Add Route Button */}
                  <button
                    onClick={handleCreateRoute}
                    className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-medium rounded-md shadow-sm transition-all duration-200"
                  >
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Route
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-2">
              {filteredRoutes.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No routes found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm || statusFilter !== 'all' ?
                      'Try adjusting your search or filter criteria.' :
                      'Get started by creating your first route.'
                    }
                  </p>
                  {!searchTerm && statusFilter === 'all' && (
                    <div className="mt-6">
                      <button
                        onClick={handleCreateRoute}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      >
                        <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Route
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Card View */}
                  {view === 'card' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredRoutes.map(renderRouteCard)}
                    </div>
                  )}

                  {/* List View */}
                  {view === 'list' && (
                    <div className="overflow-x-auto">
                      <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Route
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Stops/Days
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Schedule
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredRoutes.map(renderRouteRow)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded shadow-2xl border border-gray-100 w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Route</h3>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete <span className="font-medium text-gray-900">"{showDeleteConfirm.name}"</span>?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteRoute(showDeleteConfirm)}
                    className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded transition-colors"
                  >
                    Delete Route
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteList;